import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import { useContext, useState } from "react";
import { useNavigate } from "react-router";
import LoadingButton from "./LoadingButton";
import TitleStylizedUnderline from "./TitleStylizedUnderline";
import { IdentityProviderClientContext } from "../../services/api/contexts";
import { useSnackbar } from "../layout/SnackbarContext";

export default function MFAChallenge({ description }: { readonly description: string }) {
  const identityProviderClient = useContext(IdentityProviderClientContext);
  const { showSnackbar } = useSnackbar();
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (code) {
      setLoading(true);
      try {
        await identityProviderClient.confirmSignIn(code);
      } catch (err: any) {
        if (err.code === "CodeMismatchException") {
          setLoading(false);
          showSnackbar({ text: "Invalid code", color: "danger" });
          return;
        } else if (err.code === "ExpiredCodeException") {
          showSnackbar({
            text: "Code expired. Please try again.",
            color: "danger",
          });
          return;
        }
        throw err;
      } finally {
        setLoading(false);
      }
      navigate("/");
    } else {
      showSnackbar({ text: "No code provided", color: "danger" });
    }
  };

  return (
    <Box>
      <Stack sx={{ mt: 2, textAlign: "center", transition: "all .33s ease-out" }} spacing={4}>
        <Typography level="h2">
          <TitleStylizedUnderline>One more step</TitleStylizedUnderline>
        </Typography>
        <Typography level="body-md">{description}</Typography>
        <Input
          size="lg"
          value={code ?? ""}
          onChange={(e) => {
            setCode(e.target.value);
          }}
        />
        <LoadingButton
          loading={loading}
          type="submit"
          onClick={() => handleSubmit()}
          fullWidth
          onKeyUp={(e: React.KeyboardEvent<HTMLButtonElement>) => {
            if (e.key === "Enter") {
              handleSubmit();
            }
          }}
        >
          Send code
        </LoadingButton>
      </Stack>
    </Box>
  );
}
